import _ from 'lodash';
import {
  lsStorageService,
  lsStorageServiceConstants,
  lsApiService,
  lsErrorService,
} from 'ls-vue-common';
import moment from 'moment';
import Fuse from 'fuse.js';

import {
  PROPERTY_MANAGER__GET_PROPERTIES,
  PROPERTY_MANAGER__PROPERTIES,
  PROPERTY_MANAGER__MUTATE_CURRENT_PROPERTY_ID,
  PROPERTY_MANAGER__UPDATE_CURRENT_PROPERTY_ID,
  PROPERTY_MANAGER__MUTATE_PROPERTIES,
  PROPERTY_MANAGER__MUTATE_SEARCH_QUERY,
  PROPERTY_MANAGER__UPDATE_SEARCH_QUERY,
  PROPERTY_MANAGER__MUTATE_LOADING_STATUS,
  PROPERTY_MANAGER__SHOW_SINGLE_PROPERTY,
  PROPERTY_MANAGER__CURRENT_PROPERTY_ID,
  AUTH__IS_PROPERTY_MANAGER,
  PROPERTY_MANAGER__MUTATE_SORT,
  PROPERTY_MANAGER__UPDATE_SORT,
  PROPERTY_MANAGER__MUTATE_FILTER,
  PROPERTY_MANAGER__RESET,
} from '@/store/types';
import { attachPropertyStatusAndNextService } from './property-manager.helpers';
import { lsModuleHelpers } from '../helpers';

const fuseOptions = {
  threshold: 0.1,
  location: 0,
  distance: 300,
  maxPatternLength: 32,
  minMatchCharLength: 2,
};

const createInitialState = () => {
  return {
    currentPropertyID: null,
    properties: [],
    searchString: '',
    filter: [],
    sorting: { type: '', order: '' },
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
  };
};

export const mutations = {
  [PROPERTY_MANAGER__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },
  [PROPERTY_MANAGER__MUTATE_CURRENT_PROPERTY_ID](state, payload) {
    state.currentPropertyID = payload;
  },
  [PROPERTY_MANAGER__MUTATE_PROPERTIES](state, payload) {
    state.properties = payload;
  },
  [PROPERTY_MANAGER__MUTATE_SEARCH_QUERY](state, payload) {
    state.searchString = payload;
  },
  [PROPERTY_MANAGER__MUTATE_SORT](state, payload) {
    state.sorting = payload;
  },
  [PROPERTY_MANAGER__MUTATE_FILTER](state, payload) {
    state.filter = payload;
  },
  [PROPERTY_MANAGER__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },
};

export const actions = {
  [PROPERTY_MANAGER__GET_PROPERTIES]({ commit, rootState }) {
    commit(PROPERTY_MANAGER__MUTATE_LOADING_STATUS, { isLoading: true });

    const { customer_id } = rootState.auth.user;

    lsApiService
      .request({
        method: 'GET',
        route: `/customers/${customer_id}/properties`,
        params: {
          include: ['schedules'],
        },
      })
      .then(({ data }) => {
        const propertiesWithDetails = _.map(data, (property) => {
          return {
            details: attachPropertyStatusAndNextService({ property }),
            property,
          };
        });

        commit(PROPERTY_MANAGER__MUTATE_PROPERTIES, propertiesWithDetails);
      })
      .catch((error) => {
        lsErrorService.log({
          error,
          showAlert: true,
        });
      })
      .finally(() => {
        commit(PROPERTY_MANAGER__MUTATE_LOADING_STATUS, { isLoading: false });
      });
  },
  [PROPERTY_MANAGER__UPDATE_CURRENT_PROPERTY_ID]({ commit }, { propertyID }) {
    lsStorageService.set(lsStorageServiceConstants.CURRENT_PROPERTY_ID, propertyID);

    // don't set stored at value if propertyID is null upon login
    if (propertyID) {
      lsStorageService.set(
        lsStorageServiceConstants.CURRENT_PROPERTY_ID_STORED_AT,
        moment().toDate()
      );
    }

    commit(PROPERTY_MANAGER__MUTATE_CURRENT_PROPERTY_ID, propertyID);
  },
  [PROPERTY_MANAGER__UPDATE_SEARCH_QUERY]({ commit }, { query }) {
    commit(PROPERTY_MANAGER__MUTATE_SEARCH_QUERY, query);
  },
  [PROPERTY_MANAGER__UPDATE_SORT]({ commit, state }, { type }) {
    const { sorting } = state;

    const newSort = { type, order: 'asc' };

    if (sorting.type === type) {
      if (sorting.order === 'asc') {
        newSort.order = 'desc';
      } else {
        newSort.type = '';
        newSort.order = '';
      }
    }

    commit(PROPERTY_MANAGER__MUTATE_SORT, newSort);
  },
};

export const getters = {
  [PROPERTY_MANAGER__PROPERTIES](state) {
    let filteredProperties = [...state.properties];

    if (state.searchString) {
      filteredProperties = _.filter(filteredProperties, (section) => {
        const address = _.concat(
          section.property.street,
          section.property.zip,
          section.property.state,
          section.property.city
        );

        const fuse = new Fuse(address, fuseOptions);
        const results = fuse.search(state.searchString);

        if (results.length) {
          return section;
        }

        return;
      });
    }

    if (state.filter.length) {
      const { filter } = state;

      filteredProperties = _.filter(filteredProperties, (section) => {
        if (_.includes(filter, section.details.class)) {
          return section;
        }
        return;
      });
    }

    const { type } = state.sorting;

    if (type) {
      return _.orderBy(
        filteredProperties,
        (section) => {
          if (type === 'id') {
            return section.property.property_id;
          }
          // we want to sort by street name so get rid of numbers, spaces and /
          const streetWithoutNumbers = section.property.street.replace(/^[\s\d/]+/, '');

          return streetWithoutNumbers;
        },
        state.sorting.order
      );
    }

    return filteredProperties;
  },
  [PROPERTY_MANAGER__CURRENT_PROPERTY_ID](state) {
    if (state.currentPropertyID) {
      return state.currentPropertyID;
    }

    const currentPropertyIdInStorage = lsStorageService.get(
      lsStorageServiceConstants.CURRENT_PROPERTY_ID
    );

    const currentPropertyIdStoredAt = lsStorageService.get(
      lsStorageServiceConstants.CURRENT_PROPERTY_ID_STORED_AT
    );

    if (currentPropertyIdStoredAt && moment().diff(currentPropertyIdStoredAt, 'hours') < 2) {
      return currentPropertyIdInStorage;
    }

    return null;
  },
  [PROPERTY_MANAGER__SHOW_SINGLE_PROPERTY](state, getters, rootState, rootGetters) {
    return Boolean(
      rootGetters[AUTH__IS_PROPERTY_MANAGER] && getters[PROPERTY_MANAGER__CURRENT_PROPERTY_ID]
    );
  },
};

export const propertyManager = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
